export type PackageType = "vial" | "pen";
export type MiniPrice = { amount: number; currency: string } | null;
export type MiniProduct = {
  id: string; sku: string; name: string; description: string | null; pack_size: string | null; supports_pen: boolean;
  availability: string; availability_code: "in" | "low" | "restocking" | "out";
  restock_eta: string | null;
  can_notify_restock: boolean; stock_alert_active: boolean;
  orderable: { vial: boolean; pen: boolean };
  prices: { vial: MiniPrice; pen: MiniPrice };
};
export type MiniCartLine = { id: string; product_id: string; package_type: PackageType; quantity: number; unit_price: number; subtotal: number };
export type MiniSavedOrder = {
  id: string; name: string; pricing_mode_used: "retail" | "reseller"; created_at: string; updated_at: string;
  items: Array<{ product_id: string; product_name: string; sku: string; package_type: PackageType; quantity: number; unit_price: number }>;
};
export type MiniOrderStatus = "new" | "contacted" | "pending_payment" | "payment_received" | "confirmed" | "completed" | "cancelled";
export type MiniReview = { id: string; order_request_id: string; rating: number; comment: string; status: "pending" | "approved" | "rejected"; created_at: string };
export type MiniRecentOrder = {
  id: string; status: MiniOrderStatus; total_eur: number; created_at: string; updated_at: string;
  tracking_carrier: string | null; tracking_number: string | null; tracking_url: string | null; shipped_at: string | null;
  review: MiniReview | null; can_review: boolean;
};
export type MiniSupportTicket = {
  id: string; order_request_id: string | null; category: "order" | "delivery" | "payment" | "account" | "stock" | "other";
  subject: string; message: string; status: "open" | "in_progress" | "waiting_customer" | "resolved" | "closed";
  admin_reply: string | null; replied_at: string | null; created_at: string; updated_at: string;
};
export type MiniApprovedReview = Pick<MiniReview, "id" | "rating" | "comment" | "created_at">;
export type MiniAppState = {
  user: { first_name: string; pricing_mode: "retail" | "reseller"; is_reseller: boolean; telegram_handle: string | null };
  products: MiniProduct[];
  cart: { lines: MiniCartLine[]; quantity: number; total: number };
  saved_orders: MiniSavedOrder[];
  recent_orders: MiniRecentOrder[];
  support_tickets: MiniSupportTicket[];
  approved_reviews: MiniApprovedReview[];
};
export type MiniCartState = Pick<MiniAppState, "cart">;

const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:8787";

export async function publicCatalogRequest<T>(): Promise<T> {
  const response = await fetch(`${apiBase}/public/catalog`);
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(body.error ?? `Pedido falhou (${response.status})`);
  return body as T;
}

export async function miniAppRequest<T>(path: string, initData: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: { "content-type": "application/json", "x-telegram-init-data": initData, ...init.headers }
  });
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(body.error ?? `Pedido falhou (${response.status})`);
  return body as T;
}

export async function storeRequest<T>(path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiBase}${path.replace(/^\/mini-app/, "/store")}`, {
    ...init,
    headers: { "content-type": "application/json", authorization: `Bearer ${accessToken}`, ...init.headers }
  });
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(body.error ?? `Pedido falhou (${response.status})`);
  return body as T;
}
